// Wire DTO → domain conversion. Keeps `Date` instances in app state; ISO strings stay
// at the network boundary. Pure functions; no side effects.

import type {
  PagedResult,
  RequestDetail,
  RequestDetailDto,
  RequestListItem,
  RequestListItemDto,
  Stage,
  StageDto,
  Task,
  TaskAction,
  TaskActionDto,
  TaskDto,
} from "@gw/shared-types";

function parseDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function parseStage(dto: StageDto): Stage {
  return {
    name: dto.name,
    key: dto.key,
    start: parseDate(dto.start),
    end: parseDate(dto.end),
    bd: dto.bd,
    cd: dto.cd,
    isRunning: dto.isRunning,
  };
}

export function parseRequestListItem(dto: RequestListItemDto): RequestListItem {
  return {
    ...dto,
    requestCreatedDateTime: parseDate(dto.requestCreatedDateTime) ?? new Date(NaN),
    requestReleasedDateTime: parseDate(dto.requestReleasedDateTime),
    requestCompletedDateTime: parseDate(dto.requestCompletedDateTime),
    workOrderCreatedDateTime: parseDate(dto.workOrderCreatedDateTime),
    workOrderSubmitDateTime: parseDate(dto.workOrderSubmitDateTime),
    workOrderApproveDatetime: parseDate(dto.workOrderApproveDatetime),
    preOnboardingStartDateTime: parseDate(dto.preOnboardingStartDateTime),
    completedAt: parseDate(dto.completedAt),
    linkedOnboardingCreatedDateTime: parseDate(dto.linkedOnboardingCreatedDateTime),
    bottleneckStage: dto.bottleneckStage ? parseStage(dto.bottleneckStage) : null,
  };
}

export function parseRequestListPage(
  page: PagedResult<RequestListItemDto>,
): PagedResult<RequestListItem> {
  return {
    items: page.items.map(parseRequestListItem),
    page: page.page,
    pageSize: page.pageSize,
    totalCount: page.totalCount,
    totalPages: page.totalPages,
    summary: page.summary,
  };
}

export function parseTaskAction(dto: TaskActionDto): TaskAction {
  return {
    ...dto,
    actionAssignedAt: parseDate(dto.actionAssignedAt),
    actionStartDatetime: parseDate(dto.actionStartDatetime),
    actionCompletedDatetime: parseDate(dto.actionCompletedDatetime),
  };
}

export function parseTask(dto: TaskDto): Task {
  return {
    ...dto,
    taskCreatedDateTime: parseDate(dto.taskCreatedDateTime),
    startDatetime: parseDate(dto.startDatetime) ?? new Date(NaN),
    completedDatetime: parseDate(dto.completedDatetime),
    taskCompletedDatetime: parseDate(dto.taskCompletedDatetime),
    actions: dto.actions.map(parseTaskAction),
  };
}

export function parseRequestDetail(dto: RequestDetailDto): RequestDetail {
  return {
    ...parseRequestListItem(dto),
    stages: dto.stages.map(parseStage),
    intake: dto.intake
      ? {
          ...dto.intake,
          startedDateTime: parseDate(dto.intake.startedDateTime) ?? new Date(dto.intake.startedDateTime),
          endedDateTime: parseDate(dto.intake.endedDateTime),
        }
      : null,
    tasks: dto.tasks.map(parseTask),
    timeBreakdown: dto.timeBreakdown ?? null,
  };
}
